/**
 * "How this page works" keys (Baker, 2026-09-25: so Robin can find his way
 * around without a walkthrough) + the notice shown when a feature is paused.
 * Open by default the first time; the open/closed choice is remembered per
 * browser (a convenience only - nothing depends on it).
 */
import { useState } from 'react';

const GUIDES: Record<string, { title: string; points: string[] }> = {
  home: {
    title: 'Home',
    points: [
      'My day: what is being worked on now (today\'s plan plus anything in Development), new requests waiting for review, anything stuck more than 7 days, and anything waiting on someone else.',
      'From your email: emails that look like requests. Accept turns one into a New request (with a link back to the email); Dismiss hides it for good.',
      'Company snapshot: open requests per division - click a division to see its requests on the Kanban.',
      'Click any request title to open it.',
    ],
  },
  kanban: {
    title: 'Kanban',
    points: [
      'Each column is a group of stages. Click a column title to open it up into its individual stages; click again to close it.',
      'Drag a card to another column to move it to that stage. Drop on Done to mark it complete.',
      'Use the filters to see one division, one type of request, or one person\'s asks.',
      'An orange edge means the request has sat in its stage for more than 7 days.',
    ],
  },
  lineup: {
    title: 'Lineup',
    points: [
      'The priority order for everything that has been reviewed. Higher Impact x Urgency score goes higher by default.',
      'Drag a row (grab the dots on the left) to move it up or down - it gets a "pinned" tag so the score won\'t move it back.',
      'Re-sort by score puts everything back in score order and removes the pins (it asks first).',
      'The dashed line shows roughly where one week of work runs out, based on estimated hours.',
      'Dragging is paused while a filter is on - clear the filters to reorder.',
    ],
  },
  org: {
    title: 'Org board',
    points: [
      'The company chart with each division\'s open requests. An orange top edge means that division has urgent requests.',
      'Click a division box to see its requests; click a request to open it.',
      'Company-wide requests (ones that help everyone) sit in the band at the top.',
    ],
  },
  planner: {
    title: 'Planner',
    points: [
      'Drag requests from the tray on the right into Today, Tomorrow or This week. The bar shows planned hours against the day or week.',
      'Click the circle on a Today item to log hours: "Log + keep" leaves it on Today, "Log + clear" takes it off the plan. Logging hours never changes the request\'s stage.',
      'Anything left on Today rolls over to the next day (marked "rolled over"). Tomorrow items move to Today on their own.',
      'New requests only show up in the tray once they have been reviewed.',
    ],
  },
  done: {
    title: 'Completed',
    points: [
      'Release notes: what was delivered, newest first, with the note sent to the person who asked.',
      'Stats: how much is getting done and how long it takes. "Show as table" gives the numbers.',
      'Search archive: find anything completed by words, division, type, person or date.',
    ],
  },
  admin: {
    title: 'Admin',
    points: [
      'People show up here after they sign in once. Set their division (it pre-fills their request form) and role.',
      'Editors (Baker, Robin) can see and change everything. Staff can only submit and follow their own requests.',
      'You can\'t remove your own editor access here (so nobody locks themselves out).',
      'Capacity sets the hours used by the Planner and the Lineup\'s week line; the tier numbers set what counts as Critical / High / Medium.',
    ],
  },
  detail: {
    title: 'A request',
    points: [
      'Change the stage with the dropdown at the top, or click any stage in the Stages list.',
      'Set Impact and Urgency (1-5 each) to score it; the score decides its default place in the Lineup.',
      'Everything saves on its own when you click away from a field.',
      'Add a note in Activity to leave a comment - it is dated and shows who wrote it.',
      'At Release, write the release note: it goes to the person who asked when the request is completed.',
    ],
  },
};

const KEY = 'hub.guide.';

export function Guide({ page }: { page: keyof typeof GUIDES | string }) {
  const g = GUIDES[page];
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(KEY + page) !== 'closed'; } catch { return true; }
  });
  if (!g) return null;
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(KEY + page, next ? 'open' : 'closed'); } catch { /* private window etc. */ }
  };
  return (
    <div className={`guide ${open ? 'open' : ''}`}>
      <button className="guide-toggle" onClick={toggle} aria-expanded={open}>
        <span className="guide-q">?</span> How the {g.title} page works <span className="muted small">{open ? 'hide' : 'show'}</span>
      </button>
      {open && <ul>{g.points.map((p) => <li key={p}>{p}</li>)}</ul>}
    </div>
  );
}

/** Shown when a feature is paused by the current state (e.g. dragging while filtered). */
export function Paused({ children }: { children: React.ReactNode }) {
  return <div className="paused" role="status"><b>Paused:</b> {children}</div>;
}
